import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { UserCircleIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { shadows } from '../design-system/tokens/shadows';
import { breakpoints } from '../design-system/tokens/breakpoints';
import { User } from '../types/auth';

const MenuWrap = styled.div`
  position: relative;
  z-index: 2;
  flex-shrink: 0;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: ${spacing[2]};
  min-width: 44px;
  min-height: 44px;
  max-width: 220px;
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  transition: background 150ms ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background: ${props => props.theme.colors.surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const TriggerLabel = styled.span`
  display: none;
  min-width: 0;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: ${breakpoints.lg}) {
    display: inline;
  }
`;

const Dropdown = styled.div<{ $top: number; $right: number }>`
  position: fixed;
  top: ${props => props.$top}px;
  right: ${props => props.$right}px;
  min-width: 200px;
  max-width: min(280px, calc(100vw - 16px));
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  box-shadow: ${shadows.lg};
  z-index: 1400;
  overflow: hidden;
`;

const UserInfo = styled.div`
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const UserName = styled.p`
  margin: 0;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
`;

const UserEmail = styled.p`
  margin: ${spacing[1]} 0 0 0;
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  word-break: break-word;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: none;
  background: none;
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${props => props.theme.colors.surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: -2px;
  }
`;

interface UserMenuProps {
  user: User;
  onLogout: () => void;
}

interface DropdownCoords {
  top: number;
  right: number;
}

export const UserMenu: React.FC<UserMenuProps> = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<DropdownCoords>({ top: 0, right: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleReposition = () => updatePosition();

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePosition]);

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

  return (
    <MenuWrap ref={wrapRef}>
      <Trigger
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-label={t('nav.profile')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <UserCircleIcon width={22} height={22} aria-hidden />
        <TriggerLabel>{displayName}</TriggerLabel>
      </Trigger>
      {open &&
        createPortal(
          <Dropdown
            ref={dropdownRef}
            role="menu"
            $top={coords.top}
            $right={coords.right}
          >
            <UserInfo>
              <UserName>{displayName}</UserName>
              {user.email && <UserEmail>{user.email}</UserEmail>}
            </UserInfo>
            <MenuItem
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
            >
              <ArrowRightOnRectangleIcon width={18} height={18} aria-hidden />
              {t('nav.logout')}
            </MenuItem>
          </Dropdown>,
          document.body
        )}
    </MenuWrap>
  );
};

export default UserMenu;
